/**
 * Quality Detector
 * 
 * Handles network quality assessment and performance monitoring.
 * Provides bandwidth estimation, latency testing, quality scoring,
 * performance metrics collection, and quality trend analysis.
 */

import { ConnectionStatus } from '@capacitor/network';
import { NetworkQuality, NetworkEvent, NetworkEventListener } from './BaseNetworkService';

/**
 * Quality Detector
 * 
 * Responsible for monitoring network quality through latency tests and
 * performance metrics. Provides quality scoring and trend analysis.
 */
export class QualityDetector {
  private eventListeners: Set<NetworkEventListener> = new Set();
  private qualityMonitorInterval: NodeJS.Timeout | null = null;
  private currentQuality: NetworkQuality | null = null;
  private qualityHistory: NetworkQuality[] = [];
  private isMonitoring = false;
  
  // Configuration
  private qualityCheckInterval: number;
  private qualityCheckTimeout: number;
  private qualityCheckUrl: string;
  private enableDebugLogging: boolean;

  constructor(
    qualityCheckInterval = 30000,
    qualityCheckTimeout = 5000,
    qualityCheckUrl = '/api/health',
    enableDebugLogging = true
  ) {
    this.qualityCheckInterval = qualityCheckInterval;
    this.qualityCheckTimeout = qualityCheckTimeout;
    this.qualityCheckUrl = qualityCheckUrl;
    this.enableDebugLogging = enableDebugLogging;
  }

  /**
   * Start quality monitoring
   */
  startMonitoring(currentStatus: ConnectionStatus | null): void {
    if (this.qualityMonitorInterval || !currentStatus?.connected) return;

    this.debugLog('📊 Starting network quality monitoring...');

    this.qualityMonitorInterval = setInterval(async () => {
      await this.checkNetworkQuality(currentStatus);
    }, this.qualityCheckInterval);

    this.isMonitoring = true;

    // Initial quality check
    this.checkNetworkQuality(currentStatus);
    
    this.debugLog('✅ Network quality monitoring started');
  }

  /**
   * Stop quality monitoring
   */
  stopMonitoring(): void {
    if (this.qualityMonitorInterval) {
      clearInterval(this.qualityMonitorInterval);
      this.qualityMonitorInterval = null;
      this.isMonitoring = false;
      
      this.debugLog('🛑 Network quality monitoring stopped');
    }
  }

  /**
   * Add event listener
   */
  addEventListener(listener: NetworkEventListener): void {
    this.eventListeners.add(listener);
  }

  /**
   * Remove event listener
   */
  removeEventListener(listener: NetworkEventListener): void {
    this.eventListeners.delete(listener);
  }

  /**
   * Remove all event listeners
   */
  removeAllEventListeners(): void {
    this.eventListeners.clear();
  }

  /**
   * Get current network quality
   */
  getCurrentQuality(): NetworkQuality | null {
    return this.currentQuality;
  }

  /**
   * Get quality history
   */
  getQualityHistory(): NetworkQuality[] {
    return [...this.qualityHistory];
  }

  /**
   * Get average latency from history
   */
  getAverageLatency(): number {
    if (this.qualityHistory.length === 0) return 0;
    
    const totalLatency = this.qualityHistory.reduce((sum, q) => sum + q.latency, 0);
    return totalLatency / this.qualityHistory.length;
  }

  /**
   * Check network quality
   */
  private async checkNetworkQuality(currentStatus: ConnectionStatus | null): Promise<void> {
    if (!currentStatus?.connected) return;

    try {
      const startTime = Date.now();
      
      const response = await fetch(this.qualityCheckUrl, {
        method: 'HEAD',
        signal: AbortSignal.timeout(this.qualityCheckTimeout)
      });
      
      const latency = Date.now() - startTime;
      
      let quality: NetworkQuality['quality'];
      if (!response.ok) {
        quality = 'poor';
      } else if (latency < 200) {
        quality = 'excellent';
      } else if (latency < 500) {
        quality = 'good';
      } else {
        quality = 'poor';
      }

      const newQuality: NetworkQuality = {
        quality,
        latency,
        timestamp: new Date().toISOString(),
        connectionType: currentStatus.connectionType
      };

      this.debugLog('📊 Network quality check:', newQuality);

      // Check if quality changed
      if (this.currentQuality && this.currentQuality.quality !== quality) {
        this.debugLog('🔄 Network quality changed:', {
          from: this.currentQuality.quality,
          to: quality,
          latency
        });
        
        this.emitEvent({
          type: 'network_quality_changed',
          data: {
            quality,
            latency
          }
        });
      }

      this.currentQuality = newQuality;
      this.addToHistory(newQuality);

    } catch (error) {
      // Network quality check failed
      const newQuality: NetworkQuality = {
        quality: 'offline',
        latency: this.qualityCheckTimeout,
        timestamp: new Date().toISOString(),
        connectionType: currentStatus.connectionType
      };

      this.debugLog('❌ Network quality check failed:', error);

      if (this.currentQuality && this.currentQuality.quality !== 'offline') {
        this.emitEvent({
          type: 'network_quality_changed',
          data: {
            quality: 'offline',
            latency: this.qualityCheckTimeout
          }
        });
      }

      this.currentQuality = newQuality;
      this.addToHistory(newQuality);
    }
  }

  /**
   * Add quality measurement to history
   */
  private addToHistory(quality: NetworkQuality): void {
    this.qualityHistory.push(quality);

    // Keep only last 100 quality measurements
    if (this.qualityHistory.length > 100) {
      this.qualityHistory = this.qualityHistory.slice(-100);
    }
  }

  /**
   * Check if new quality is better than old quality
   */
  isQualityBetter(newQuality: string, oldQuality: string): boolean {
    const qualityOrder = ['offline', 'poor', 'good', 'excellent'];
    return qualityOrder.indexOf(newQuality) > qualityOrder.indexOf(oldQuality);
  }

  /**
   * Get quality trend (improving, declining, stable)
   */
  getQualityTrend(): 'improving' | 'declining' | 'stable' {
    if (this.qualityHistory.length < 3) return 'stable';

    const recent = this.qualityHistory.slice(-3);
    const qualityOrder = ['offline', 'poor', 'good', 'excellent'];
    
    const scores = recent.map(q => qualityOrder.indexOf(q.quality));
    const firstScore = scores[0];
    const lastScore = scores[scores.length - 1];

    if (lastScore > firstScore) return 'improving';
    if (lastScore < firstScore) return 'declining';
    return 'stable';
  }

  /**
   * Update configuration
   */
  updateConfig(updates: {
    qualityCheckInterval?: number;
    qualityCheckTimeout?: number;
    qualityCheckUrl?: string;
  }): void {
    const needsRestart = this.isMonitoring && updates.qualityCheckInterval && 
                        updates.qualityCheckInterval !== this.qualityCheckInterval;

    if (updates.qualityCheckInterval) this.qualityCheckInterval = updates.qualityCheckInterval;
    if (updates.qualityCheckTimeout) this.qualityCheckTimeout = updates.qualityCheckTimeout;
    if (updates.qualityCheckUrl) this.qualityCheckUrl = updates.qualityCheckUrl;

    // Restart monitoring if interval changed
    if (needsRestart) {
      this.stopMonitoring();
      // Note: Caller should restart monitoring with current status
    }
  }

  /**
   * Reset quality history
   */
  resetHistory(): void {
    this.qualityHistory = [];
    this.currentQuality = null;
    this.debugLog('🧹 Quality history reset');
  }

  /**
   * Emit event to all listeners
   */
  private emitEvent(event: NetworkEvent): void {
    this.eventListeners.forEach(listener => {
      try {
        listener(event);
      } catch (error) {
        console.error('Error in quality detector event listener:', error);
      }
    });
  }

  /**
   * Debug logging helper
   */
  private debugLog(message: string, data?: unknown): void {
    if (this.enableDebugLogging) {
      if (data) {
        console.log(message, data);
      } else {
        console.log(message);
      }
    }
  }

  /**
   * Get monitoring status
   */
  isCurrentlyMonitoring(): boolean {
    return this.isMonitoring;
  }

  /**
   * Cleanup resources
   */
  cleanup(): void {
    this.stopMonitoring();
    this.removeAllEventListeners();
    this.resetHistory();
    
    this.debugLog('🧹 Quality detector cleaned up');
  }
}
